
import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, SimpleChanges, OnChanges } from '@angular/core';
import { slideInDownAnimation } from '../../../app/app.animation';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { SelectionModel } from '@angular/cdk/collections';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { IPaymentProcessor } from '../../interfaces';
import * as fromPaymentConfig from '../../../screens/payment-config/state/payment-config.selectors';
import * as PaymentConfigAction from '../../../screens/payment-config/state/payment-config.actions';
import * as moment from 'moment';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AngularMaterialService } from 'src/commons';

@Component({
    selector: 'payment-config-table-component',
    templateUrl: 'payment-config-table-component.html',
    styleUrls: ['payment-config-table-component.scss'],
    animations: [slideInDownAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentConfigTableComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @Input() tableType: string;
    @Input() data: any[];
    @Input() private paymentProcessorData$: Observable<IPaymentProcessor[] | null>;

    public displayedColumns = [];
    public dataSource: MatTableDataSource<any>;
    localData: any[];
    isPristine = true;
    isDeleting = false;
    datePipe = new DatePipe('en-US');
    paymentProcessors: any;
    paymentProcessorDataSubscription: Subscription;
    selection = new SelectionModel<any>(true, []);
    selectedFormData: any[] = [];
    isShowVAlidate: boolean = false;

    constructor(
        private ref: ChangeDetectorRef,
        private ams: AngularMaterialService,
        private route: ActivatedRoute,
        private router: Router,
        private store: Store<fromPaymentConfig.State>
    ) {
        this.selectState();
    }

    public ngOnInit() {
        this.startSubscriptions();
        switch (this.tableType) {
            case 'paymentProcessor':
                this.displayedColumns = ['paymentProcessor', 'paymentOptions'];
                break;
            case 'paymentOption':
                this.displayedColumns = ['paymentOption', 'status'];
                break;
            case 'taxExemptStatus':
                this.displayedColumns = ['taxExemptStatus', 'status', 'taxExemptDescr'];
                break;
            case 'form':
                this.displayedColumns = ['formType', 'taxExemptStatus', 'paymentProcessor', 'paymentOptions', 'startDate', 'endDate', 'delete'];
                break;
        }
        setTimeout(() => {
            this.localData = JSON.parse(JSON.stringify(this.data));
            this.buildDataSource(this.localData);
            this.ref.markForCheck();
        });
    }

    private selectState(): void {
        this.paymentProcessorData$ = this.store.select(fromPaymentConfig.getPaymentProcessorState);
    }

    private startSubscriptions(): void {
        this.paymentProcessorDataSubscription = this.paymentProcessorData$
            .subscribe(data => {
                if (data != null) {
                    this.paymentProcessors = data;
                    this.ref.markForCheck();
                }
            })
    }

    getPaymentOptions(value) {
        if (this.paymentProcessors !== undefined) {
            const po = this.paymentProcessors.find(pp => pp.paymentProcessor === value);
            return po.paymentOptions
        }
    }

    buildDataSource(data: any) {
        const filter = this.dataSource !== undefined ? this.dataSource.filter : '';
        this.dataSource = new MatTableDataSource(data);
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'taxExemptStatus': return isNaN(item.subsection) ? item.subsection : +item.subsection;
                case 'startDate': return moment(item.startDate);
                case 'endDate': return moment(item.endDate);
                default: return item[property];
            }
        };
        this.dataSource.filterPredicate = (data, filter: string) => {
            const accumulator = (currentTerm, key) => {
                switch (key) {
                    case 'startDate':
                        const newStartDate = this.datePipe.transform(data.startDate, 'MM-dd-yyyy');
                        return currentTerm + newStartDate.toString();
                    case 'endDate':
                        const newEndDate = this.datePipe.transform(data.endDate, 'MM-dd-yyyy');
                        return currentTerm + newEndDate.toString();
                    case 'subsection':
                        return currentTerm + this.getTaxExemptStatus(data.subsection);
                    default:
                        return currentTerm + data[key];
                }
            };
            const dataStr = Object.keys(data).reduce(accumulator, '').toLowerCase();
            const transformedFilter = filter.trim().toLowerCase();
            return dataStr.includes(transformedFilter);
        };
        this.dataSource.filter = filter;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes['data']) {
            this.isPristine = true;
            setTimeout(() => {
                this.localData = JSON.parse(JSON.stringify(changes['data'].currentValue));
                this.buildDataSource(this.localData);
                this.ref.markForCheck();
            });
        }

    }

    onAdd() {
        let configDetail: any = {};
        configDetail.ID = -1;
        configDetail.formType = '';
        configDetail.subsection = '';
        configDetail.paymentProcessor = '';
        configDetail.paymentOptions = [];
        configDetail.startDate = moment().startOf('day');
        configDetail.endDate = moment().endOf('day').subtract(1, 'seconds');
        configDetail.deleteIt = 0;
        const title = this.tableType === 'form' ? 'Form Payment Configurations' : 'Payment Processor Details'

        this.ams.showPaymentConfigDetails(title, this.tableType, configDetail);
        this.ams.paymentConfig.afterClosed().subscribe(result => {
            if (result) {
                if (result) {
                    this.store.dispatch(new PaymentConfigAction.UpdatePaymentConfigState(result));
                }
            }
        });
    }

    onDelete() {
        this.isDeleting = true;
    }

    selectRow(row) {
        let configDetail: any = {};
        if (this.isDeleting) {
            this.isDeleting = false;
            this.ams.showConfirm('Are you sure to delete this configuration?', ' ');
            this.ams.confirm.afterClosed().subscribe(result => {
                if (result === 'yes') {
                    configDetail.ID = row.ID;
                    configDetail.formType = row.formType,
                        configDetail.subsection = row.subsection,
                        configDetail.paymentProcessor = row.paymentProcessor,
                        configDetail.paymentOptions = this.getPaymentOptions(row.paymentProcessor),
                        configDetail.startDate = row.startDate,
                        configDetail.endDate = row.endDate
                    configDetail.deleteIt = 1;
                    this.store.dispatch(new PaymentConfigAction.UpdatePaymentConfigState(configDetail));
                }
            });
        } else {
            if (this.tableType === 'form' || this.tableType === 'paymentProcessor') {
                if (this.tableType === 'form') {
                    configDetail.ID = row.ID;
                    configDetail.formType = row.formType,
                        configDetail.subsection = row.subsection,
                        configDetail.paymentProcessor = row.paymentProcessor,
                        configDetail.paymentOptions = this.getPaymentOptions(row.paymentProcessor),
                        configDetail.startDate = row.startDate,
                        configDetail.endDate = row.endDate;
                    configDetail.deleteIt = 0;
                } else {
                    configDetail.paymentProcessor = row.paymentProcessor;
                    configDetail.paymentOptions = row.paymentOptions;
                }
                const title = this.tableType === 'form' ? 'Form Payment Config Details' : 'Payment Processor Details'
                this.ams.showPaymentConfigDetails(title, this.tableType, configDetail);
                this.ams.paymentConfig.afterClosed().subscribe(result => {
                    if (result) {
                        if (this.tableType === 'form') {
                            this.store.dispatch(new PaymentConfigAction.UpdatePaymentConfigState(result));
                        } else {
                            let updatedDetail = this.localData.find(r => r.paymentProcessor === row.paymentProcessor);
                            updatedDetail.paymentOptions = result.paymentOptions;
                            this.store.dispatch(new PaymentConfigAction.UpdatePaymentProcessorState(this.localData))
                            this.buildDataSource(this.localData);
                        }
                        this.ref.markForCheck();
                    }
                })
            }
        }
    }

    toggleActiveStatus(row): void {
        let toggledItem: any;
        this.isPristine = false;
        switch (this.tableType) {
            case 'taxExemptStatus':
                toggledItem = this.localData.find(item => item.subsection === row.subsection);
                break;
            case 'paymentOption':
                toggledItem = this.localData.find(item => item.paymentOption === row.paymentOption);
                break;
        }

        toggledItem.isActive = toggledItem.isActive === 1 ? 0 : 1;
        this.buildDataSource(this.localData);
        this.ref.markForCheck();
    }

    onSave() {
        switch (this.tableType) {
            case 'taxExemptStatus':
                this.store.dispatch(new PaymentConfigAction.UpdateSubsectionsState(this.localData));
                break;
            case 'paymentOption':
                this.store.dispatch(new PaymentConfigAction.UpdatePaymentOptionState(this.localData));
                break;
        }
        this.isPristine = true;
    }

    onCancel() {
        if (!this.isPristine) {
            this.ams.showConfirm('Are you sure you want to cancel?', 'Your unsaved change(s) will be discarded');
            this.ams.confirm.afterClosed().subscribe(result => {
                if (result === 'yes') {
                    this.isPristine = true;
                    this.localData = JSON.parse(JSON.stringify(this.data));
                    this.buildDataSource(this.localData);
                    this.ref.markForCheck();
                }
            });
        }
    }

    stringify(paymentOptions) {

        return paymentOptions === undefined || paymentOptions === 0 ? '' : paymentOptions.join(', ');
    }

    getTaxExemptStatus(value) {
        if (value === '')
            return 'Other';
        else if (isNaN(value) || +value === 0) {
            return value;
        } else {
            return `501(c)(${+value})`;
        }
    }

    getFormName(value) {
        switch (value) {
            case 'CC':
                return 'Charitable Contribution (CC)';
            case 'MG':
                return 'Matching Gift (MG)';
            case 'CG':
                return 'Community Grants (CG)';
            case 'CI':
                return 'Community Investment (CI)';
            case 'FO':
                return 'LG&E and KU Foundation (FO)';
            case 'PP':
                return 'Plant for The Planet (PP)';
            case 'ES':
                return 'Event Sponsorship (ES)';
            default:
                return 'Charitable Contribution (CC)';
        }
    }

    public applyFilter(filterValue: string) {
        filterValue = filterValue.trim();
        filterValue = filterValue.toLowerCase();
        this.dataSource.filter = filterValue;
    }
        
    ngOnDestroy() {
        this.paymentProcessorDataSubscription.unsubscribe();
    }

}
